/**
 * Component Name: SectionGroupedTable
 * Description: A table component that groups invoices by section\\subsection and displays aggregated data with sortable columns and clickable section names.
 * Props:
 *   - filteredInvoices (array): List of filtered invoice objects to group by sections.
 *   - projects (array): List of project objects for mapping project IDs to names.
 *   - handleSectionClick (function): Callback to handle clicks on section names for drill-down.
 * Feature: Displays section-level invoice data, including total invoice amounts, subtotals, and PO counts, with support for variations and clickable section names to view associated purchase orders in the invoice management interface.
 */
import React from 'react';
import CustomTable from '../table/table';
import { toUSD } from '../../util/currency-formatter';
import { toDecimal, roundValue } from '../../util/decimal-utils';
import { GSTORVAT } from '../../util/enums';
import { INVOICE_CSS_CLASSES, INVOICE_STATUS, INVOICE_TABLE_COLUMNS } from '../../util/enums/invoice-management';

const SectionGroupedTable = ({ filteredInvoices, projects, handleSectionClick }) => {
    const groupedData = [];
    const sectionMap = new Map();

    // Group invoices by section\\subsection
    filteredInvoices.forEach((inv) => {
        const project = projects.find((p) => p.id === inv.projectId);
        const key = inv.stageId === 'variations' ? `variations-${inv.projectId}` : inv.stageId;
        const sectionName = inv.sectionHierarchy || INVOICE_STATUS.NA;
        if (!sectionMap.has(key)) {
            sectionMap.set(key, {
                sectionId: inv.stageId,
                projectId: inv.projectId,
                projectName: project?.name || INVOICE_STATUS.NA,
                sectionName,
                totalAmount: toDecimal(0),
                invoiceCount: 0,
                invoiceSubTotal: toDecimal(0),
                poTotal: toDecimal(0),
                uniquePOIds: new Set(), // Track unique PO IDs instead of count
            });
        }
        const section = sectionMap.get(key);
        section.totalAmount = section.totalAmount.plus(toDecimal(inv.totalAmount || 0));
        section.invoiceCount += 1;
        section.invoiceSubTotal = section.invoiceSubTotal.plus(toDecimal(inv.subTotal || 0));
        section.poTotal = section.poTotal.plus(toDecimal(inv.PurchaseOrders?.total || 0));

        // Track unique PO ID instead of counting each invoice
        if (inv.PurchaseOrders?.id) {
            section.uniquePOIds.add(inv.PurchaseOrders.id);
        }

        // Pre-index invoices for efficient variation lookup
        const invoicesByTask = new Map();
        filteredInvoices.forEach(inv => {
            const key = `${inv.taskId}-${inv.projectId}`;
            if (!invoicesByTask.has(key)) invoicesByTask.set(key, []);
            invoicesByTask.get(key).push(inv);
        });

        // Add project-level variations, filtered by project
        projects.forEach((project) => {
            const variationStage = project.stages.find(s => s.id === 'variations');
            if (variationStage && variationStage.variations) {
                variationStage.variations.forEach((variation) => {
                    const variationInvoices = invoicesByTask.get(`${variation.id}-${project.id}`) || [];
                    if (variationInvoices.length > 0) {
                        const totalAmount = variationInvoices.reduce((sum, inv) =>
                            sum.plus(toDecimal(inv.totalAmount || 0)), toDecimal(0));
                        const invoiceSubTotal = variationInvoices.reduce((sum, inv) =>
                            sum.plus(toDecimal(inv.subTotal || 0)), toDecimal(0));
                        const poTotal = variationInvoices.reduce((sum, inv) =>
                            sum.plus(toDecimal(inv.PurchaseOrders?.total || 0)), toDecimal(0));

                        // Count unique PO IDs for variations
                        const uniquePOIds = new Set();
                        variationInvoices.forEach(inv => {
                            if (inv.PurchaseOrders?.id) {
                                uniquePOIds.add(inv.PurchaseOrders.id);
                            }
                        });

                        sectionMap.set(`${variation.id}${project.id}`, {
                            sectionId: variation.id,
                            projectId: project.id,
                            projectName: project.name,
                            sectionName: `Variation: ${variation.name}`,
                            totalAmount: roundValue(totalAmount),
                            invoiceCount: variationInvoices.length,
                            invoiceSubTotal: roundValue(invoiceSubTotal),
                            poTotal: roundValue(poTotal),
                            poCount: uniquePOIds.size, // Use unique PO count
                            isVariation: true,
                        });
                    }
                });
            }
        });
    });

    sectionMap.forEach((value) => {
        // Convert Decimal values to numbers for display if not already converted
        if (typeof value.totalAmount !== 'number') {
            value.totalAmount = roundValue(value.totalAmount);
        }
        if (typeof value.invoiceSubTotal !== 'number') {
            value.invoiceSubTotal = roundValue(value.invoiceSubTotal);
        }
        if (typeof value.poTotal !== 'number') {
            value.poTotal = roundValue(value.poTotal);
        }

        // Convert uniquePOIds Set to poCount for display
        if (value.uniquePOIds) {
            value.poCount = value.uniquePOIds.size;
            delete value.uniquePOIds; // Remove the Set from the final data
        }

        groupedData.push(value);
    });

    const columns = [
        {
            title: INVOICE_TABLE_COLUMNS.SECTION_SUBSECTION,
            field: 'sectionName',
            render: (row) => (
                <span
                    className={INVOICE_CSS_CLASSES.SECTION_LINK}
                    onClick={() => handleSectionClick(row.sectionId, row.sectionName, row.projectId, row.projectName)}
                >
                    {row.sectionName}
                </span>
            ),
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PROJECT_NAME,
            field: 'projectName',
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_AMOUNT,
            field: 'totalAmount',
            class: 'text-right',
            render: (row) => <span className={INVOICE_CSS_CLASSES.AMOUNT}>{toUSD(row.totalAmount)}</span>,
            sortable: true,
        },
        {
            title: `${INVOICE_TABLE_COLUMNS.INVOICE_SUBTOTAL} (Ex. ${GSTORVAT})`,
            field: 'invoiceSubTotal',
            class: 'text-right',
            render: (row) => <span className={INVOICE_CSS_CLASSES.SUB_TOTAL_AMOUNT}>{toUSD(row.invoiceSubTotal)}</span>,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_COUNT,
            field: 'invoiceCount',
            class: 'text-center',
            render: (row) => <span>{row.invoiceCount}</span>,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PO_TOTAL,
            field: 'poTotal',
            class: 'text-right',
            render: (row) => <span>{toUSD(row.poTotal)}</span>,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PO_COUNT,
            field: 'poCount',
            class: 'text-right',
            render: (row) => <span>{row.poCount}</span>,
            sortable: true,
        }
    ];

    const tableItemClickHandler = (row) => {
        handleSectionClick(row.sectionId, row.sectionName, row.projectId, row.projectName);
    };

    return (
        <div className="table-container">
            <CustomTable columns={columns} data={groupedData} pagination={true} rowClickHandler={tableItemClickHandler} />
        </div>
    );
};

export default SectionGroupedTable;